<template>
	<Message severity="warn" :closable="false">
		<h3>{{ $t('Squeeze.System.Warning') }}</h3>
		{{ $t('Squeeze.System.MicrosoftAuthenticationWarningMessage') }}
	</Message>
	<div class="p-fluid p-component p-formgrid p-grid p-input-filled">
		<div class="p-field p-col-6">
			<label>{{ $t('Squeeze.System.ClientId') }} *</label>
			<InputText v-model="systemMicrosoftAuthentication.clientId" @change="update" :disabled="loading" />
			<small v-if="(v$.clientId.$invalid && showErrorMessage)" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.System.ClientId')}) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label>{{ $t('Squeeze.System.Password') }} *</label>
			<Password
				v-model="systemMicrosoftAuthentication.clientSecret.value"
				:placeholder="systemMicrosoftAuthentication.clientSecret.isSet ? '*****' : ''"
				:feedback="false"
				@change="update"
				:disabled="loading"
			/>
			<small v-if="(v$.clientSecret.value.$invalid && showErrorMessage)" class="p-error">
				{{ $t('Forms.Val.MissingRequired', { field: $t('Squeeze.System.Password')}) }}
			</small>
		</div>
		<div class="p-field p-col-6">
			<label>{{ $t('Squeeze.System.GroupIdUsers') }}</label>
			<InputText v-model="systemMicrosoftAuthentication.groupIdUsers" @change="update" :disabled="loading" />
		</div>
		<div class="p-field p-col-6">
			<label>{{ $t('Squeeze.System.GroupIdAdmins') }}</label>
			<InputText v-model="systemMicrosoftAuthentication.groupIdAdmins" @change="update" :disabled="loading" />
		</div>
	</div>
	<div class="p-fluid p-formgrid p-grid p-jc-end">
		<div class="p-field p-col-3">
			<Button
				:label="$t('Squeeze.System.ResetCredentials')"
				type="button"
				icon="mdi mdi-delete-outline"
				class="p-button p-button-danger"
				@click="saveMicrosoftAuthenticationCredentials($event, true)"
			/>
		</div>
		<div class="p-field p-col-3">
			<Button
				:label="$t('Squeeze.General.Save')"
				type="button"
				icon="mdi mdi-content-save-outline"
				class="p-button"
				@click="saveMicrosoftAuthenticationCredentials"
				:loading="loading"
			/>
		</div>
	</div>
</template>

<script lang="ts">
import {defineComponent, nextTick, onMounted, reactive, ref} from 'vue';
import {SingleSignOnCredentials} from "@dex/squeeze-client-ts";
import {ClientManager} from "@/singletons/ClientManager";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import {ToastManager} from "@/util/ToastManager";
import Message from 'primevue/message';
import InputText from "primevue/inputtext";
import Password from 'primevue/password';
import {useVuelidate} from "@vuelidate/core";
import {required, helpers} from "@vuelidate/validators";
import {FormState} from "@/util/Types";

type SingleSignOnCredentialsFormState = FormState<SingleSignOnCredentials, "clientId" | "clientSecret">;

export default defineComponent({
	name: "SystemMicrosoftAuthentication",
	components: {
		Message,
		InputText,
		Password,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();

		/** Api for authentication */
		const authenticationApi = ClientManager.getInstance().squeeze.authentication;

		/** Show Loading on load data */
		const loading = ref<boolean>(false);

		/** Object with Microsoft authentication credentials */
		const systemMicrosoftAuthentication = reactive<SingleSignOnCredentialsFormState>({
			clientId:	undefined,
			clientSecret: {
				value: '',
				isSet: false,
			},
			groupIdUsers: '',
			groupIdAdmins: '',
		});

		/** Custom validator for clientSecret - only required if not already set */
		const requiredIfNotSet = helpers.withMessage(
			() => t('Forms.Val.MissingRequired', { field: t('Squeeze.System.Password')}),
			(value: unknown) => {
				// If secret is already set, validation passes regardless of input
				if (systemMicrosoftAuthentication.clientSecret?.isSet) {
					return true;
				}
				// If not set, value must be provided
				return Boolean(helpers.req(value));
			}
		);

		/** Determines the required rules for validation */
		const rules = {
			clientId: { required },
			clientSecret: {
				value: { requiredIfNotSet },
			},
		}

		/** Use Vuelidate */
		const v$ = useVuelidate(rules, systemMicrosoftAuthentication);

		/** Triggered when (all) field values are invalid */
		const showErrorMessage = ref<boolean>(false);

		/** Get Microsoft authentication credentials */
		const getMicrosoftAuthentication = () => {
			loading.value = true;
			authenticationApi.getSingleSignOnCredentials()
				.then(data => {
					Object.assign(systemMicrosoftAuthentication, data);
				}).catch(response => response.json().then ((error: { message: string; statusCode: number }) => {
					if (error.statusCode === 500) {
						return;
					}
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + error.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/**
		 * Save microsoft authentication credentials
		 * If the “resetCredentials” parameter is set to “true”, the credentials will be reset
		 * @param event
		 * @param resetCredentials (optional)
		 */
		const saveMicrosoftAuthenticationCredentials = (event: any, resetCredentials: boolean = false) => {
			if (v$.value.$invalid && !resetCredentials) {
				showErrorMessage.value = true;
				return;
			}
			loading.value = true;

			// set the credentials to an empty string, when "resetCredentials" is true
			if (resetCredentials) {
				systemMicrosoftAuthentication.clientId = '';
				systemMicrosoftAuthentication.clientSecret = {
					value: '',
					isSet: false,
				};
				systemMicrosoftAuthentication.groupIdUsers = '';
				systemMicrosoftAuthentication.groupIdAdmins = '';
			}

			authenticationApi.setSingleSignOnCredentials(systemMicrosoftAuthentication)
				.then(() => {
					const messageKey = resetCredentials
						? 'Squeeze.System.ResetMicrosoftAuthenticationCredentials'
						: 'Squeeze.System.SaveMicrosoftAuthenticationCredentials';

					ToastManager.showSuccess(toast, t('Squeeze.General.Success'), t(messageKey));
				})
				.catch(response => response.json().then ((error: { message: string }) => {
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + error.message);
				})).finally(() => {
					loading.value = false;
				})
		}

		/** Triggered when change in input-field */
		const update = async () => {
			await nextTick(); // Otherwise the change of the fields will not be recognised fast enough
			showErrorMessage.value = v$.value.$invalid;
		}

		onMounted(() => {
			getMicrosoftAuthentication();
		})

		return {
			loading,
			systemMicrosoftAuthentication,
			v$,
			showErrorMessage,
			saveMicrosoftAuthenticationCredentials,
			update,
		};
	},
});

</script>

<style scoped>

</style>
